class QuickView {
  constructor() {
    this.modal = document.querySelector('[data-quick-view-modal]');
    this.container = this.modal?.querySelector('[data-quick-view-container]');
    this.overlay = this.modal?.querySelector('[data-quick-view-overlay]');
    this.closeBtn = this.modal?.querySelector('[data-quick-view-close]');

    this.product = null;
    this.currentOptions = [];

    this.init();
  }

  init() {
    if (!this.modal) return;

    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', this.handleTriggerClick.bind(this));
    document.addEventListener('mouseover', this.handleHover.bind(this));
    this.overlay?.addEventListener('click', this.close.bind(this));
    this.closeBtn?.addEventListener('click', this.close.bind(this));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  handleHover(event) {
    const card = event.target.closest('.product-card');
    if (!card || card.dataset.preloaded) return;

    const trigger = card.querySelector('[data-quick-view-trigger]');
    if (!trigger) return;

    const productHandle = trigger.dataset.productHandle;
    if (!productHandle) return;

    card.dataset.preloaded = 'true';

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = `/products/${productHandle}?section_id=quick-view`;
    document.head.appendChild(link);

    const img = card.querySelector('.product-card__image');
    if (img && img.src) {
      const preloadImg = new Image();
      preloadImg.src = img.src.replace(/(_\d+x\d+)?\./, '_600x.').replace('1200x1200', '600x600');
    }
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest('[data-quick-view-trigger]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const productHandle = trigger.dataset.productHandle;
    if (productHandle) {
      this.open(productHandle);
    }
  }

  async open(productHandle) {
    this.modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    this.showLoading();

    try {
      const response = await fetch(`/products/${productHandle}?section_id=quick-view`);
      if (!response.ok) throw new Error('Failed to load product');

      const html = await response.text();
      this.container.innerHTML = html;

      this.initProductData();
      this.bindProductEvents();

      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    } catch (error) {
      this.container.innerHTML = `
        <div class="quick-view__error">
          <p>Impossible de charger le produit.</p>
          <a href="/products/${productHandle}" class="btn btn--primary">Voir le produit</a>
        </div>
      `;
    }
  }

  close() {
    this.modal.classList.remove('is-active');
    document.body.style.overflow = '';
    this.product = null;
    this.currentOptions = [];
  }

  isOpen() {
    return this.modal.classList.contains('is-active');
  }

  showLoading() {
    this.container.innerHTML = `
      <div class="quick-view-modal__loading">
        <div class="collection-loading__spinner"></div>
      </div>
    `;
  }

  initProductData() {
    const jsonScript = this.container.querySelector('[data-product-json]');
    if (jsonScript) {
      this.product = JSON.parse(jsonScript.textContent);
      this.currentOptions = this.product.options.map((_, index) => {
        const activeBtn = this.container.querySelector(
          `[data-option-index="${index}"] .quick-view__swatch--active, [data-option-index="${index}"] .quick-view__size--active`
        );
        return activeBtn?.dataset.optionValue || this.product.variants[0].options[index];
      });
    }
  }

  bindProductEvents() {
    const swatches = this.container.querySelectorAll('.quick-view__swatch');
    const sizes = this.container.querySelectorAll('.quick-view__size');
    const form = this.container.querySelector('[data-quick-view-form]');

    swatches.forEach(swatch => {
      swatch.addEventListener('click', () => this.handleOptionClick(swatch));
    });

    sizes.forEach(size => {
      size.addEventListener('click', () => this.handleOptionClick(size));
    });

    form?.addEventListener('submit', this.handleSubmit.bind(this));
  }

  handleOptionClick(button) {
    if (button.classList.contains('quick-view__swatch--unavailable') ||
        button.classList.contains('quick-view__size--unavailable')) {
      return;
    }

    const optionIndex = parseInt(button.closest('[data-option-index]').dataset.optionIndex);
    const value = button.dataset.optionValue;

    this.currentOptions[optionIndex] = value;

    const container = button.closest('.quick-view__swatches, .quick-view__sizes');
    container.querySelectorAll('.quick-view__swatch--active, .quick-view__size--active').forEach(el => {
      el.classList.remove('quick-view__swatch--active', 'quick-view__size--active');
    });

    if (button.classList.contains('quick-view__swatch')) {
      button.classList.add('quick-view__swatch--active');
    } else {
      button.classList.add('quick-view__size--active');
    }

    this.updateVariant();
  }

  updateVariant() {
    const variant = this.findVariant();

    if (variant) {
      this.updatePrice(variant);
      this.updateAddButton(variant);
      this.updateVariantId(variant);
      this.updateImage(variant);
    }

    this.updateAvailability();
  }

  findVariant() {
    return this.product.variants.find(variant => {
      return variant.options.every((option, index) => option === this.currentOptions[index]);
    });
  }

  updatePrice(variant) {
    const priceContainer = this.container.querySelector('[data-quick-view-price]');
    if (!priceContainer) return;

    let html = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      html += `<span class="quick-view__price-compare">${this.formatMoney(variant.compare_at_price)}</span>`;
    }
    html += `<span class="quick-view__price-current">${this.formatMoney(variant.price)}</span>`;

    priceContainer.innerHTML = html;
  }

  updateAddButton(variant) {
    const button = this.container.querySelector('[data-quick-view-add]');
    if (!button) return;

    if (variant.available) {
      button.disabled = false;
      button.textContent = 'Ajouter au panier';
    } else {
      button.disabled = true;
      button.textContent = 'Rupture de stock';
    }
  }

  updateVariantId(variant) {
    const input = this.container.querySelector('[data-variant-id]');
    if (input) {
      input.value = variant.id;
    }
  }

  updateImage(variant) {
    if (!variant.featured_image) return;

    const image = this.container.querySelector('[data-quick-view-image]');
    if (image) {
      image.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_600x$1');
      image.alt = variant.featured_image.alt || this.product.title;
    }
  }

  updateAvailability() {
    this.product.options.forEach((option, optionIndex) => {
      const container = this.container.querySelector(`[data-option-index="${optionIndex}"]`);
      if (!container) return;

      const buttons = container.querySelectorAll('.quick-view__swatch, .quick-view__size');

      buttons.forEach(button => {
        const value = button.dataset.optionValue;
        const testOptions = [...this.currentOptions];
        testOptions[optionIndex] = value;

        const variantExists = this.product.variants.some(variant => {
          return variant.options.every((opt, idx) => opt === testOptions[idx]) && variant.available;
        });

        button.classList.toggle('quick-view__swatch--unavailable', !variantExists && button.classList.contains('quick-view__swatch'));
        button.classList.toggle('quick-view__size--unavailable', !variantExists && button.classList.contains('quick-view__size'));
      });
    });
  }

  async handleSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const variantId = form.querySelector('[data-variant-id]')?.value;
    const button = form.querySelector('[data-quick-view-add]');

    if (!variantId || !button) return;

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Ajout...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      button.textContent = 'Ajouté !';
      document.dispatchEvent(new CustomEvent('cart:item-added'));

      setTimeout(() => {
        this.close();
      }, 1000);

    } catch (error) {
      button.textContent = 'Erreur';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new QuickView();
});
